import {getSiteUrl} from "@/helpers/functions";
import {coerceImageObjects, type ImageInput} from "@/helpers/imageObject";

export interface ArticleAuthor {
    name: string;
    url?: string;
}

export interface EntityReference {
    "@id": string;
}

export interface SeoSchemaArticleProps {
    title: string;
    description: string;
    link: string;
    body: string;
    featuredImage: ImageInput | ImageInput[];
    datePublished: string;
    dateModified?: string;
    articleSection?: string;
    keywords?: string[];
    author?: ArticleAuthor;
    about?: EntityReference[];
}

const ARTICLE_BODY_MAX_LENGTH = 5000;
const HEADLINE_MAX_LENGTH = 110;

const stripHtml = (html: string): string =>
    html
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\s+/g, " ")
        .trim();

const truncate = (value: string, max: number): string => {
    if (value.length <= max) return value;
    const slice = value.slice(0, max);
    const lastSpace = slice.lastIndexOf(" ");
    return (lastSpace > max * 0.6 ? slice.slice(0, lastSpace) : slice).trimEnd();
};

const toIso = (value: string): string => {
    if (!value) return "";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toISOString();
};

const toAbsoluteUrl = (siteUrl: string, url: string): string => {
    if (/^https?:\/\//i.test(url)) return url;
    const path = url.startsWith("/") ? url : `/${url}`;
    return `${siteUrl}${path}`;
};

export function SeoSchemaArticle({
    title,
    description,
    body,
    link,
    featuredImage,
    datePublished,
    dateModified,
    articleSection,
    keywords,
    author,
    about,
}: SeoSchemaArticleProps) {
    const siteUrl = getSiteUrl();
    const absoluteLink = toAbsoluteUrl(siteUrl, link);
    const articleBody = stripHtml(body).slice(0, ARTICLE_BODY_MAX_LENGTH);
    const headline = truncate(title, HEADLINE_MAX_LENGTH);
    const published = toIso(datePublished);
    const modified = toIso(dateModified || datePublished);

    const imageList: ImageInput[] = Array.isArray(featuredImage) ? featuredImage : [featuredImage];
    const imageNodes = coerceImageObjects(
        imageList.filter((img): img is ImageInput => typeof img === "string" ? Boolean(img) : Boolean(img?.src))
    );

    const orgRef = {"@id": `${siteUrl}/#organization`};

    const authorNode = author?.name
        ? {
              "@type": "Person",
              "name": author.name,
              ...(author.url ? {url: toAbsoluteUrl(siteUrl, author.url)} : {}),
          }
        : orgRef;

    const schema: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        "@id": absoluteLink,
        "mainEntityOfPage": absoluteLink,
        "headline": headline,
        "name": title,
        "description": description,
        "author": authorNode,
        "publisher": orgRef,
        "articleBody": articleBody,
        "speakable": {
            "@type": "SpeakableSpecification",
            "xpath": [
                "/html/head/title",
                "/html/body//*[contains(concat(' ', normalize-space(@class), ' '), ' lead ')]",
                "/html/body//h1",
            ],
        },
    };

    if (imageNodes.length) schema.image = imageNodes;
    if (published) schema.datePublished = published;
    if (modified) schema.dateModified = modified;
    if (articleSection) schema.articleSection = articleSection;
    if (keywords && keywords.length) schema.keywords = keywords.join(", ");
    if (about && about.length) schema.about = about;

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{__html: JSON.stringify(schema)}}
        />
    );
}
